import { ReactNode, useCallback, useRef } from 'react';

import {
  FormFieldsHandle,
  FormFieldsProvider,
} from '../../context/FormFieldsContext';
import Content, { ContentProps } from './Content';
import SubmitError from './SubmitError';

export type FormProps<T extends Record<string, unknown>> = Omit<
  ContentProps,
  'component' | 'onSubmit' | 'children'
> & {
  initialValues: T;
  onSubmit: (values: T) => void | Promise<void>;
  children: ReactNode;
};

export const Form = <T extends Record<string, unknown>>({
  initialValues,
  onSubmit,
  children,
  ...contentProps
}: FormProps<T>) => {
  const formRef = useRef<FormFieldsHandle<T>>(null);

  const handleSubmit = useCallback(async () => {
    if (!formRef.current) {
      throw new Error('Form is not ready');
    }
    await onSubmit(formRef.current.values as T);
  }, [onSubmit]);

  return (
    <Content component="form" onSubmit={handleSubmit} data-track-source-anchor="0ad2ef6f7f;form-content" {...contentProps}>
      <FormFieldsProvider ref={formRef} initialValues={initialValues}>
        <SubmitError />
        {children}
      </FormFieldsProvider>
    </Content>
  );
};

Form.displayName = 'DialogComposite.Form';
